import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic } from 'antd';
import {
  FileTextOutlined, SyncOutlined, CheckCircleOutlined,
  WarningOutlined, BellOutlined, MailOutlined,
} from '@ant-design/icons';
import { orderApi, emailApi, appealApi, claimApi } from '../services/api';
import { useNavigate } from 'react-router-dom';

// 与调度概览同款 KPI 卡：一类工单一组，每组 6 张，点击跳对应流转列表
function KpiCard({ title, value, icon, color, flash, onClick }) {
  return (
    <Col xs={12} sm={8}>
      <Card className="card-shadow dashboard-kpi-card" onClick={onClick} hoverable>
        <Statistic
          title={title}
          value={value}
          prefix={icon}
          valueStyle={{ color }}
          formatter={(v) => <span className={flash && v > 0 ? 'stat-flash' : ''}>{v}</span>}
        />
      </Card>
    </Col>
  );
}

function KpiGroup({ title, cards }) {
  return (
    <div style={{ marginTop: 24 }}>
      <h3 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 600 }}>{title}</h3>
      <Row gutter={[16, 16]}>
        {cards.map((c) => <KpiCard key={c.title} {...c} />)}
      </Row>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ pending: 0, processing: 0, completed: 0, urgent: 0, awaiting: 0 });
  const [emailCount, setEmailCount] = useState(0);
  const [appealStats, setAppealStats] = useState({});
  const [claimStats, setClaimStats] = useState({});
  const [allowed, setAllowed] = useState(new Set());

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      // 先取当前账号可见模块，未开通的模块不再发请求，避免工作台被模块门禁拦成 403
      let keys = new Set(['dashboard', 'orders', 'emails', 'appeals', 'claims']);
      try {
        const res = await fetch(`${import.meta.env.VITE_API_URL || ''}/api/admin/frontend-modules/config`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
        });
        const cfg = await res.json();
        keys = new Set((cfg.items || []).map((i) => i.module_key));
      } catch (e) { /* 取不到配置时保持默认，交给后续接口兜底 */ }
      setAllowed(keys);
      const hasOrders = keys.has('orders');
      const hasEmails = keys.has('emails');
      const hasAppeals = keys.has('appeals');
      const hasClaims = keys.has('claims');
      const [pending, processing, awaiting, completed, urgent, emailCnt, appealSum, claimSum] = await Promise.all([
        hasOrders ? orderApi.list({ status: 'pending', page_size: 1 }) : null,
        hasOrders ? orderApi.list({ status: 'processing', page_size: 1 }) : null,
        hasOrders ? orderApi.list({ status: 'awaiting_customer', page_size: 1 }) : null,
        hasOrders ? orderApi.list({ status: 'completed', page_size: 1 }) : null,
        // 紧急模块 = 未处理（pending）中的紧急单，处理完/结束自动移出
        hasOrders ? orderApi.list({ status: 'pending', priority: 'urgent', page_size: 1 }) : null,
        hasEmails ? emailApi.unreadCount() : null,
        hasAppeals ? appealApi.summary() : null,
        hasClaims ? claimApi.summary() : null,
      ]);
      setStats({
        pending: pending?.total || 0,
        processing: processing?.total || 0,
        awaiting: awaiting?.total || 0,
        completed: completed?.total || 0,
        urgent: urgent?.total || 0,
      });
      setEmailCount(emailCnt?.count || 0);
      setAppealStats(appealSum || {});
      setClaimStats(claimSum || {});
    } catch (e) {}
  };

  const num = (v) => v || 0;
  const ICON_COLOR = {
    pending: '#faad14', processing: '#3E6AE1', awaiting: '#722ed1',
    mail: '#ff4d4f', urgent: '#ff4d4f', completed: '#52c41a',
  };

  // 调度概览
  const orderCards = [
    { title: '调度待跟进工单', value: stats.pending, icon: <FileTextOutlined style={{ color: ICON_COLOR.pending }} />, color: ICON_COLOR.pending, flash: true, onClick: () => navigate('/orders/pending') },
    { title: '调度处理中工单', value: stats.processing, icon: <SyncOutlined style={{ color: ICON_COLOR.processing }} />, color: ICON_COLOR.processing, onClick: () => navigate('/orders/processing') },
    { title: '待客服处理工单', value: stats.awaiting, icon: <BellOutlined style={{ color: ICON_COLOR.awaiting }} />, color: ICON_COLOR.awaiting, flash: true, onClick: () => navigate('/orders/all?status=awaiting_customer') },
    { title: '待识别邮件', value: emailCount, icon: <MailOutlined style={{ color: ICON_COLOR.mail }} />, color: ICON_COLOR.mail, flash: true, onClick: () => navigate('/emails/inbox') },
    { title: '紧急订单', value: stats.urgent, icon: <WarningOutlined style={{ color: ICON_COLOR.urgent }} />, color: ICON_COLOR.urgent, flash: true, onClick: () => navigate('/orders/urgent') },
    { title: '调度完成', value: stats.completed, icon: <CheckCircleOutlined style={{ color: ICON_COLOR.completed }} />, color: ICON_COLOR.completed, onClick: () => navigate('/orders/completed') },
  ];

  // 今日申诉工单概览（与调度概览同款，仅统计范围换成申诉工单）
  const appealCards = [
    { title: '申诉待跟进工单', value: num(appealStats.pending), icon: <FileTextOutlined style={{ color: ICON_COLOR.pending }} />, color: ICON_COLOR.pending, flash: true, onClick: () => navigate('/appeals/pending') },
    { title: '申诉处理中工单', value: num(appealStats.processing), icon: <SyncOutlined style={{ color: ICON_COLOR.processing }} />, color: ICON_COLOR.processing, onClick: () => navigate('/appeals/processing') },
    { title: '待客服处理工单', value: num(appealStats.awaiting_customer), icon: <BellOutlined style={{ color: ICON_COLOR.awaiting }} />, color: ICON_COLOR.awaiting, flash: true, onClick: () => navigate('/appeals/awaiting-customer') },
    { title: '待识别邮件', value: emailCount, icon: <MailOutlined style={{ color: ICON_COLOR.mail }} />, color: ICON_COLOR.mail, flash: true, onClick: () => navigate('/emails/inbox') },
    { title: '申诉紧急工单', value: num(appealStats.urgent_count), icon: <WarningOutlined style={{ color: ICON_COLOR.urgent }} />, color: ICON_COLOR.urgent, flash: true, onClick: () => navigate('/appeals/urgent') },
    { title: '申诉完成', value: num(appealStats.completed), icon: <CheckCircleOutlined style={{ color: ICON_COLOR.completed }} />, color: ICON_COLOR.completed, onClick: () => navigate('/appeals/completed') },
  ];

  // 今日索赔工单概览
  const claimCards = [
    { title: '索赔待跟进工单', value: num(claimStats.pending), icon: <FileTextOutlined style={{ color: ICON_COLOR.pending }} />, color: ICON_COLOR.pending, flash: true, onClick: () => navigate('/claims/pending') },
    { title: '索赔处理中工单', value: num(claimStats.processing), icon: <SyncOutlined style={{ color: ICON_COLOR.processing }} />, color: ICON_COLOR.processing, onClick: () => navigate('/claims/processing') },
    { title: '待客服处理工单', value: num(claimStats.awaiting_customer), icon: <BellOutlined style={{ color: ICON_COLOR.awaiting }} />, color: ICON_COLOR.awaiting, flash: true, onClick: () => navigate('/claims/awaiting-customer') },
    { title: '待识别邮件', value: emailCount, icon: <MailOutlined style={{ color: ICON_COLOR.mail }} />, color: ICON_COLOR.mail, flash: true, onClick: () => navigate('/emails/inbox') },
    { title: '索赔紧急工单', value: num(claimStats.urgent_count), icon: <WarningOutlined style={{ color: ICON_COLOR.urgent }} />, color: ICON_COLOR.urgent, flash: true, onClick: () => navigate('/claims/urgent') },
    { title: '索赔完成', value: num(claimStats.completed), icon: <CheckCircleOutlined style={{ color: ICON_COLOR.completed }} />, color: ICON_COLOR.completed, onClick: () => navigate('/claims/completed') },
  ];

  // 未开通「待识别邮件」模块的账号不展示邮件卡（点进去也会被门禁拦截）
  const visibleCards = (cards) => cards.filter((c) => allowed.has('emails') || c.title !== '待识别邮件');

  return (
    <div>
      <div className="page-header">
        <h2>工作台</h2>
        <p className="desc">今日工单工作概览，快速掌握调度 / 申诉 / 索赔三类工单状态和待办事项</p>
      </div>

      {allowed.has('orders') && <KpiGroup title="今日调度工作概览" cards={visibleCards(orderCards)} />}
      {allowed.has('appeals') && <KpiGroup title="今日申诉工单概览" cards={visibleCards(appealCards)} />}
      {allowed.has('claims') && <KpiGroup title="今日索赔工单概览" cards={visibleCards(claimCards)} />}
    </div>
  );
}
